import { all, call, put, select, takeEvery } from "redux-saga/effects"
import { DckActionTypes, DckSelectors } from "dck-redux"
import store from "store"

import * as ItemTypes from "../../items/types"
import apiServerApi from "../apiServerApi"
import apiServerConstants from "../apiServerConstants"
import ProcessTypes from "../../processes/types"
import * as ActionTypes from "../../actions/types"
import { makeSagaQuery } from "../utils"
import {
  CustomerType,
  formatCollateralName,
  getBusinessName,
  getCustomerFullName,
  getDefaultPagedResult,
  HTTP_VERB,
  LoanCollateralEnum,
  LoanGroupEnum,
  LoanStatus
} from "../../../lib/utils"
import moment from "moment"
import Authenticated from "../../../lib/Authenticated"
import { manageBundleItem } from "./bundles"

function manageLoanItem(item) {

  const { customerType, business, customer, bundle, createdBy, assignTo,
    disburseDate, imeis, group } = item

  item.isBusiness = customerType === CustomerType.BUSINESS
  item.clientName = item.isBusiness ? getBusinessName(business) : getCustomerFullName(customer)
  item.bundleName = bundle ? bundle.name : ''
  item.createdByName = createdBy ? createdBy.name : ''
  item.assignToName = assignTo ? assignTo.name : ''
  item.disburseDateFormatted = moment(disburseDate).format('ll')

  item.imeisList = imeis && imeis.length ? imeis.join(', ') : ''

  item.collateralName = bundle ? formatCollateralName(bundle.loanCollateral) : ''

  const affBusiness = item.isBusiness ? business : customer ? customer.business : null
  item.affiliated = affBusiness ? `${affBusiness.name} ${affBusiness.businessId}` : ''
  item.affiliationId = affBusiness ? affBusiness.id : null

  item.isDefaulted = group === LoanGroupEnum.DEFAULTED

  if (bundle) {
    item.bundle = manageBundleItem(bundle)
  }

  return item
}

/**
 * Load loans saga
 * @param {*} param0 
 * @returns 
 */
function* loadLoansSaga({ filteringOptions }) {

  const authenticated = new Authenticated(),
  userCanOpenDevices = authenticated.canOpenDevice(),

  loanUrl = `${apiServerConstants.host}/loans/filter`,

  loanIdsForDetails = [],

  resp = yield makeSagaQuery(
    loanUrl,
    HTTP_VERB.POST,
    JSON.stringify(filteringOptions),
    ProcessTypes.LOAD_LOANS_PROCESS,
    true,
    response => {

      const data = response.body
      data.content = data.content.map(item => {

        item = manageLoanItem(item)
        const { id, bundle, status, deviceOpen, bundleQty, fineractId } = item

        // Search for 'Open Device' status
        if(userCanOpenDevices && status === LoanStatus.COMPLETED && !deviceOpen
          && bundle && bundle.loanCollateral === LoanCollateralEnum.DEVICE) {
          loanIdsForDetails.push({
            id,
            bundleQty,
            fineractId,
          })
        }

        return item
      })

      return {
        type: DckActionTypes.ITEMS_SET,
        itemType: ItemTypes.Loan,
        data
      }
    }
  )

  if (!resp) {
    return
  }

  // Dispatch an action to load extra details
  if (userCanOpenDevices) {

    yield put({
      type: ActionTypes.LOAD_ALL_DETAILS_ACTION,
      loanIds: loanIdsForDetails
    })
  }
}

function* loanAllDetailsSaga({ loanIds }) {

  if(!(loanIds && loanIds.length)) {
    return
  }

  try {

    const state = store.get("sessionData"),

    ids = loanIds.map(({ fineractId }) => fineractId),

    result = (yield call(apiServerApi.filterFullyRepaidLoans, state.access_token, ids)).body,
    data = loanIds.filter(({ fineractId }) => result.indexOf(fineractId) > -1)

    yield put({
      type: DckActionTypes.ITEMS_SET,
      itemType: ItemTypes.DevicesToOpen,
      data
    })
  }
  catch(e) {
    console.error(e)
  }
}

function* changeLoanStatus({ loanId, newStatus }) {

  const resp = yield makeSagaQuery(
    `${apiServerConstants.host}/loans/setStatus?loanId=${loanId}&newStatus=${newStatus}`, 
    HTTP_VERB.POST,
    null,
    ProcessTypes.CHANGE_LOAN_STATUS_PROCESS,
    true
  )

  if (resp) {
    yield call(reloadLoan, { id: loanId })
  }
}

function* resubmitLoanSaga({ loanId, newStatus }) {

  const resp = yield makeSagaQuery(
    `${apiServerConstants.host}/loans/resubmitloan?loanId=${loanId}&newStatus=${newStatus}`, 
    HTTP_VERB.POST,
    null,
    ProcessTypes.RESUBMIT_LOAN_PROCESS,
    true,
    response => ({
      type: ActionTypes.RESUBMIT_LOAN_RESULT,
      itemType: ItemTypes.Loan,
      data: response.body
    })
  )

  if (resp) {
    yield call(reloadLoan, { id: loanId })
  }
}

function* assignToNewUserSaga({ loanId, username }) {

  const resp = yield makeSagaQuery(
    `${apiServerConstants.host}/loans/assignTo?loanId=${loanId}&username=${username}`, 
    HTTP_VERB.POST,
    null,
    ProcessTypes.ASSIGN_LOAN_PROCESS,
    true, 
    response => ({
      type: ActionTypes.ASSIGN_LOAN_RESULT,
      itemType: ItemTypes.Loan,
      data: response.body
    })
  )

  if (resp) {
    yield call(reloadLoan, { id: loanId })
  }
}

function *loadLoanDetailsSaga({ loanId }) {

  yield makeSagaQuery(
    `${apiServerConstants.host}/loans/${loanId}/retrievedetails`, 
    HTTP_VERB.GET,
    null,
    ProcessTypes.LOAD_LOAN_DETAILS_PROCESS,
    true,
    response => ({
      type: DckActionTypes.ITEMS_SET,
      itemType: ItemTypes.LoanDetails,
      data: [{ id: loanId, data: JSON.parse(response.body) }]
    })
  )
}

function *getCustomerLoansSaga({ customerId }) {

  yield makeSagaQuery(
    `${apiServerConstants.host}/loans/getcustomerloans/${customerId}`, 
    HTTP_VERB.GET,
    null,
    ProcessTypes.GET_CUSTOMER_LOANS_PROCESS,
    true,
    response => ({
      type: DckActionTypes.ITEMS_SET,
      itemType: ItemTypes.CustomerLoans,
      data: response.body
    })
  )
}

function *getBusinessLoansSaga({ businessId }) {

  yield makeSagaQuery(
    `${apiServerConstants.host}/loans/getbusinessloans/${businessId}`, 
    HTTP_VERB.GET,
    null,
    ProcessTypes.GET_BUSINESS_LOANS_PROCESS,
    true,
    response => ({
      type: DckActionTypes.ITEMS_SET,
      itemType: ItemTypes.BusinessLoans,
      data: response.body
    })
  )
}

function *reloadLoan({ id }) {

  const response = yield makeSagaQuery(
    `${apiServerConstants.host}/loans/${id}`, 
    HTTP_VERB.GET,
    null,
    ProcessTypes.RELOAD_LOAN_PROCESS,
    true
  )

  if (response) {
    yield call(setLoan, { id, data: response.body })
  }
}

function *setLoan({ id, data }) {

  const originalPagedLoans = yield select(state => DckSelectors.selectAllItems(state, ItemTypes.Loan)),

  pagedLoans = originalPagedLoans instanceof Array ? getDefaultPagedResult() : originalPagedLoans,
  index = pagedLoans.content.findIndex(elt => elt.id === id),
  fullData = manageLoanItem(data)

  if (index > -1) {
    pagedLoans.content[index] = fullData
  }
  else {
    pagedLoans.content.push(fullData)
  }

  yield put({
    type: DckActionTypes.ITEMS_SET,
    itemType: ItemTypes.Loan,
    data: pagedLoans
  })
}

function* updateLoanGroupSaga({ loanId, group }) {

  const resp = yield makeSagaQuery(
    `${apiServerConstants.host}/loans/${loanId}/updategroup?group=${group}`, 
    HTTP_VERB.POST,
    null,
    ProcessTypes.UPDATE_LOAN_GROUP_PROCESS,
    true,
    response => ({
      type: ActionTypes.UPDATE_LOAN_GROUP_RESULT,
      itemType: ItemTypes.Loan,
      data: response.body
    })
  )

  if (resp) {
    yield call(setLoan, { id: loanId, data: resp.body })
  }
}

function* loansSaga() {

  yield all([

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEMS_LOAD &&
        action.itemType === ItemTypes.Loan,
      loadLoansSaga
    ),

    takeEvery(ActionTypes.LOAD_ALL_DETAILS_ACTION, loanAllDetailsSaga),

    takeEvery(
      action =>
        action.type === ProcessTypes.CHANGE_LOAN_STATUS,
      changeLoanStatus
    ),

    takeEvery(ActionTypes.RESUBMIT_LOAN_ACTION, resubmitLoanSaga),

    takeEvery(ActionTypes.ASSIGN_LOAN_TO_USER_ACTION, assignToNewUserSaga),

    takeEvery(ActionTypes.LOAD_LOAN_DETAILS_ACTION, loadLoanDetailsSaga),
  
    takeEvery(ActionTypes.GET_CUSTOMER_LOANS_ACTION, getCustomerLoansSaga),

    takeEvery(ActionTypes.GET_BUSINESS_LOANS_ACTION, getBusinessLoansSaga),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEM_SET &&
        action.itemType === ItemTypes.Loan,
      setLoan
    ),

    takeEvery(
      action =>
        action.type === DckActionTypes.ITEM_RELOAD &&
        action.itemType === ItemTypes.Loan,
      reloadLoan
    ),

    takeEvery(ActionTypes.UPDATE_LOAN_GROUP_ACTION, updateLoanGroupSaga),     
  ])
}

export default loansSaga



// WEBPACK FOOTER //
// ./src/redux/sagas/items/loans.js